/**
 * Admin API Service
 *
 * Status: [IMPLEMENTED]
 * Task: T2.8.6 - Implement API call
 * Task: T2.11.4 - Frontend Regenerate Flow
 *
 * Handles all API calls for the admin resume generator.
 */

const AUTH_TOKEN_KEY = 'admin_auth_token';

/**
 * Get the stored auth token
 */
export const getAuthToken = () => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

/**
 * Store the auth token
 */
export const setAuthToken = (token) => {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
};

/**
 * Clear the auth token (logout)
 */
export const clearAuthToken = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
};

/**
 * Check if user is authenticated
 */
export const isAuthenticated = () => {
  return !!getAuthToken();
};

/**
 * Login with password
 * @param {string} password - The admin password
 * @returns {Promise<{success: boolean, token?: string, error?: string}>}
 */
export const login = async (password) => {
  try {
    const response = await fetch('/api/admin/login', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ password }),
    });

    const data = await response.json();

    if (!response.ok) {
      // Extract error message - backend returns {detail: {error: {code, message}}}
      const errorObj = data.detail?.error;
      const errorMessage = typeof errorObj === 'object' ? errorObj.message : (errorObj || data.detail || 'Invalid password');
      return {
        success: false,
        error: errorMessage,
      };
    }

    // Store the token
    if (data.token) {
      setAuthToken(data.token);
    }

    return {
      success: true,
      token: data.token,
    };
  } catch (err) {
    return {
      success: false,
      error: 'Network error. Please check your connection.',
    };
  }
};

/**
 * Logout - clear the auth token
 */
export const logout = () => {
  clearAuthToken();
};

/**
 * Generate a resume
 * @param {Object} params
 * @param {string} params.companyName - The company name
 * @param {string} params.jobPosting - The job posting text
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const generateResume = async ({ companyName, jobPosting }) => {
  const token = getAuthToken();

  if (!token) {
    return {
      success: false,
      error: 'Not authenticated',
      unauthorized: true,
    };
  }

  try {
    const response = await fetch('/api/admin/generate-resume', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        company_name: companyName,
        job_posting: jobPosting,
      }),
    });

    const data = await response.json();

    if (response.status === 401 || response.status === 403) {
      clearAuthToken();
      return {
        success: false,
        error: 'Session expired. Please login again.',
        unauthorized: true,
      };
    }

    if (response.status === 429) {
      return {
        success: false,
        error: `Rate limit exceeded. Try again in ${data.retry_after || 'a few'} minutes.`,
      };
    }

    if (!response.ok) {
      // Extract error message - backend returns {detail: {error: {code, message}}}
      const errorObj = data.detail?.error;
      const errorMessage = typeof errorObj === 'object' ? errorObj.message : (errorObj || data.detail || 'Failed to generate resume');
      return {
        success: false,
        error: errorMessage,
      };
    }

    return {
      success: true,
      data: {
        pdfBase64: data.pdf_base64,
        filename: data.pdf_filename || 'Raja-Sleem-Resume.pdf',
        changesMd: data.changes_md || '',
        changes: data.changes || {},
        generatedAt: data.changes?.generated_at,
        jobTitle: data.changes?.job_title,
        rawContent: data.raw_content || null,
      },
    };
  } catch (err) {
    return {
      success: false,
      error: 'Network error. Please check your connection.',
    };
  }
};

/**
 * Regenerate a resume from edited content (F2.11)
 *
 * Uses the edited JSON content to regenerate the PDF without
 * calling the LLM - only the PDF builder is invoked.
 *
 * @param {Object} params
 * @param {string} params.companyName - The company name
 * @param {string} params.jobPosting - The job posting text
 * @param {Object} params.editedContent - The edited JSON content
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export const regenerateResume = async ({ companyName, jobPosting, editedContent }) => {
  const token = getAuthToken();

  if (!token) {
    return {
      success: false,
      error: 'Not authenticated',
      unauthorized: true,
    };
  }

  try {
    const response = await fetch('/api/admin/generate-resume', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({
        company_name: companyName,
        job_posting: jobPosting,
        edited_content: editedContent,
      }),
    });

    const data = await response.json();

    if (response.status === 401 || response.status === 403) {
      clearAuthToken();
      return {
        success: false,
        error: 'Session expired. Please login again.',
        unauthorized: true,
      };
    }

    if (response.status === 400) {
      // Validation error from edited content
      const errorObj = data.detail?.error;
      const errorMessage = typeof errorObj === 'object'
        ? (errorObj.details || errorObj.message)
        : (errorObj || data.detail || 'Invalid content');
      return {
        success: false,
        error: errorMessage,
      };
    }

    if (response.status === 429) {
      return {
        success: false,
        error: `Rate limit exceeded. Try again in ${data.retry_after || 'a few'} minutes.`,
      };
    }

    if (!response.ok) {
      const errorObj = data.detail?.error;
      const errorMessage = typeof errorObj === 'object' ? errorObj.message : (errorObj || data.detail || 'Failed to regenerate resume');
      return {
        success: false,
        error: errorMessage,
      };
    }

    return {
      success: true,
      data: {
        pdfBase64: data.pdf_base64,
        filename: data.pdf_filename || 'Raja-Sleem-Resume.pdf',
        changesMd: data.changes_md || '',
        changes: data.changes || {},
        generatedAt: data.changes?.generated_at,
        jobTitle: data.changes?.job_title,
        rawContent: data.raw_content || null,
      },
    };
  } catch (err) {
    return {
      success: false,
      error: 'Network error. Please check your connection.',
    };
  }
};

/**
 * Verify the current auth token is still valid
 * @returns {Promise<boolean>}
 */
export const verifyToken = async () => {
  const token = getAuthToken();

  if (!token) {
    return false;
  }

  try {
    const response = await fetch('/api/admin/verify', {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      clearAuthToken();
      return false;
    }

    return true;
  } catch (err) {
    // Network error - don't clear token, could be temporary
    return false;
  }
};
